import React, {useEffect, useMemo, useState} from "react";
import {B2BoxType, B2Site} from "../../../data/client.gen";
import {useLocalization} from "../../../i18n";
import {useLocalStorage} from "../../../data/store";
import {useSiteApi} from "../../../data/sites";
import {useAsyncCallback} from "react-async-hook";
import {useTenant} from "../../../context/tenant";
import {useOrder} from "../../../context/order";
import {isEmail, notBlank} from "../../../utils";
import {
	Card,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Link,
	Stack,
	Typography,
	Box, Button
} from "@mui/material";
import {TextField} from "@variocube/app-ui";
import {useNavigate} from "react-router-dom";
import {DiscountInput} from "../../../component/discount-input";

interface OrderFormProps {
	site: B2Site;
	box: B2BoxType|undefined;
}

type InputField = 'name'|'phone'|'email';

export function OrderForm({site, box}: OrderFormProps) {
	const {
		terms,
		privacyPolicy,
		requiredBookingFields,
		optionalBookingFields,
	} = site;
	const navigate = useNavigate();
	const {t, language} = useLocalization();
	const {tenant} = useTenant();
	const {getContactData, setContactData} = useLocalStorage();
	const {submitOrder} = useSiteApi();
	const {order: { customer, ...order }, setCustomer, setDiscount} = useOrder();
	const {name = '', email = '', phone = ''} = customer;

	const {loading: submitLoading, execute: handleSubmitOrder} = useAsyncCallback(submitOrder);

	const [acceptTerms, setAcceptTerms] = useState(false);
	const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);

	useEffect(() => {
		const stored = getContactData();
		if (stored !== undefined) {
			setCustomer(stored);
		}
	}, []);

	function handleInputChange(name: InputField, value: string) {
		setCustomer({
			...customer,
			[name]: value,
		});
	}

	function showInput(name: InputField) {
		return requiredBookingFields?.includes(name) || optionalBookingFields?.includes(name);
	}

	function requiredInput(name: InputField) {
		return requiredBookingFields?.includes(name);
	}

	async function handleFormSubmit() {
		if (!tenant) return;
		console.info('Set contact data to local storage');
		setContactData({ name, phone, email });

		const {
			dates: { from, until, fromDateTime, hours },
			boxes: { types = {} },
			discount: { data, discountValid },
		} = order;
		const cart = {
			from,
			until,
			fromDateTime,
			hours,
			boxes: types,
			discountCode: discountValid && notBlank(data?.code) ? data?.code : undefined,
			name,
			phone,
			email,
			locale: language
		}
		console.info('Order request', cart);
		const booking = await handleSubmitOrder(tenant.id, site.id, cart);
		navigate(`/${tenant.id}/bookings/${booking.id}/payment`);
	}

	const canSubmit = useMemo(() => !(
		(requiredInput('name') && !name)
		|| (requiredInput('email') && !isEmail(email))
		|| (requiredInput('phone') && !phone)
		|| (!!terms && !acceptTerms)
		|| (!!privacyPolicy && !acceptPrivacyPolicy)
	), [
		requiredBookingFields, optionalBookingFields, terms, privacyPolicy,
		name, email, phone, acceptTerms, acceptPrivacyPolicy
	]);

	const isZeroAmountOrder = useMemo(() =>
			box !== undefined && box.price === 0
		, [box]);

	const inputRows = [
		...(!showInput('name') ? [] : [{
			label: t('customer.name'),
			value: name,
			onChange: (value: string) => handleInputChange('name', value),
			required: requiredInput('name')
		}]),
		...(!showInput('email') ? [] : [{
			label: t('customer.email'),
			value: email,
			onChange: (value: string) => handleInputChange('email', value),
			required: requiredInput('email')
		}]),
		...(!showInput('phone') ? [] : [{
			label: t('customer.phone'),
			value: phone,
			onChange: (value: string) => handleInputChange('phone', value),
			required: requiredInput('phone')
		}]),
	];
	const acceptanceRows = [
		...(!terms ? [] : [{
			label: (
				<>
					{t('checkout.termsConsent.lead')} <Link href={terms} target="_blank">{t('checkout.terms')}</Link> {t('checkout.termsConsent.tail')}
				</>
			),
			value: acceptTerms,
			onChange: setAcceptTerms
		}]),
		...(!privacyPolicy ? [] : [{
			label: (
				<>
					{t('checkout.privacyPolicyConsent.lead')} <Link href={privacyPolicy} target="_blank">{t('checkout.privacyPolicy')}</Link> {t('checkout.privacyPolicyConsent.tail')}
				</>
			),
			value: acceptPrivacyPolicy,
			onChange: setAcceptPrivacyPolicy
		}])
	];

	return (
		<>
			<Typography textAlign="center" variant="h1" gutterBottom>{t('orders.form.title')}</Typography>
			<Card
				sx={{
					p: '1rem',
					mb: 2
				}}
			>
				<Stack
					direction="column"
					gap={2}
				>
					{inputRows.map(({label, value, onChange, required}, i) => (
						<TextField
							key={'input+' + i}
							fullWidth
							variant="standard"
							required={required}
							label={label}
							value={value}
							onChange={onChange}
						/>
					))}
				</Stack>
			</Card>
			<Card
				sx={{
					p: '1rem',
					mb: 2
				}}
			>
				<DiscountInput
					tenantId={tenant?.id ?? ''}
					siteId={site.id}
					onDiscountResult={setDiscount}
				/>
			</Card>
			{acceptanceRows.map(({label, value, onChange}, i) => (
				<FormGroup
					key={'acceptance-' + i}
					row
				>
					<FormControlLabel
						control={<Checkbox
							checked={value}
							onChange={ev => onChange(ev.target.checked)}
							color="primary"
						/>}
						label={label}
					/>
				</FormGroup>
			))}
			<Box my={1} flexGrow={1} />
			<Button
				variant="contained"
				color="primary"
				disableElevation
				onClick={handleFormSubmit}
				disabled={!canSubmit || submitLoading}
			>
				{isZeroAmountOrder ? t('orders.overview.cta') : t('orders.form.cta')}
			</Button>
		</>
	)
}
